/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/templates/AppLayout.tsx
 * Release: v2.0.0 - Layout applicativo con header e sidebar
 * Note: UI coerente con tema scuro della release precedente
 */

import React, { useState } from 'react';
import { AppSidebar } from '../organisms/AppSidebar';
import { LogoutMenu } from '../auth/LogoutMenu';
import { useAuth } from '../auth/AuthProvider';
import { PendingEmailBanner } from '../auth/PendingEmailBanner';
import { DesktopChatWidget } from '../chat';

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin' || user?.group_id === 'superadmin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <AppSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Overlay per mobile quando sidebar è aperta */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="lg:ml-48 min-w-0">
        <header className="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-md border-b border-slate-700/50 shadow-lg">
          <div className="h-16 px-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Hamburger menu button - visible only on mobile */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Logo Cliente (Coronatek) - sinistra */}
              <img
                src="/assets/Coronatek_Logo_92dpi_200x88.png"
                alt="Coronatek Logo"
                className="h-8 w-auto object-contain hidden sm:block"
              />

              <div className="h-6 w-px bg-slate-600 hidden sm:block"></div>

              <div className="text-slate-300 text-sm font-medium">
                Turbomanager — Dalla presa in carico alla riconsegna
              </div>
              <div className="h-6 w-px bg-slate-600 hidden sm:block"></div>
              <div className="text-xs text-slate-400 hidden sm:block">
                {user?.group_id && `Gruppo: ${user.group_id}`}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-xs text-slate-400 hidden sm:block text-right">
                <div className="font-medium">{user?.name || user?.email}</div>
                <div className="text-green-400">{user?.role}</div>
              </div>
              <div className="h-8 w-px bg-slate-600"></div>
              <LogoutMenu isSuperAdmin={isSuperAdmin} />
            </div>
          </div>
        </header>

        {/* Banner verifica email pendente */}
        <PendingEmailBanner />

        <main className="px-0 sm:px-1 pb-2 pt-4">
          {children}
        </main>

        {/* Footer con logo Dialogicom */}
        <footer className="mt-8 py-4 border-t border-slate-700/50 bg-slate-900/50">
          <div className="px-4 flex items-center justify-center gap-2">
            <span className="text-sm text-slate-400">Developed by</span>
            <img
              src="/assets/Dialogicom_Logo.png"
              alt="Dialogicom"
              className="h-8 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              title="Developed by Dialogicom"
            />
          </div>
        </footer>
      </div>

      {/* Chat Widget flottante */}
      <DesktopChatWidget />
    </div>
  );
};

export default AppLayout;


